import { useEffect, useState } from 'react';
import { t } from '../lib/i18n';
import { apiOrigin } from '../lib/api-client';

/**
 * Whether the API can be reached from this tab, and how fast (context.md §136 spirit: the person
 * should never wonder whether the product is alive).
 *
 * A probe of `/health/live` every thirty seconds, timed; the browser's own online/offline events
 * in between. Green under 800 ms, amber above, red when the probe fails or the browser says it is
 * offline. The latency is what the status bell reports as "tempo de resposta".
 */

export type ConnectionState = {
  status: 'ONLINE' | 'SLOW' | 'OFFLINE';
  latencyMs: number | null;
  checkedAt: number;
};

const listeners = new Set<(state: ConnectionState) => void>();
let current: ConnectionState = { status: 'ONLINE', latencyMs: null, checkedAt: 0 };
let timer: number | undefined;

function publish(next: ConnectionState): void {
  current = next;
  for (const listener of listeners) listener(next);
}

async function probe(): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    publish({ status: 'OFFLINE', latencyMs: null, checkedAt: Date.now() });
    return;
  }

  const started = performance.now();

  try {
    const response = await fetch(`${apiOrigin()}/health/live`, {
      cache: 'no-store',
      signal: AbortSignal.timeout(8_000),
    });
    const latencyMs = Math.round(performance.now() - started);

    publish({
      status: !response.ok ? 'OFFLINE' : latencyMs > 800 ? 'SLOW' : 'ONLINE',
      latencyMs,
      checkedAt: Date.now(),
    });
  } catch {
    publish({ status: 'OFFLINE', latencyMs: null, checkedAt: Date.now() });
  }
}

function ensureProbing(): void {
  if (timer !== undefined) return;

  void probe();
  timer = window.setInterval(() => void probe(), 30_000);
  window.addEventListener('online', () => void probe());
  window.addEventListener('offline', () =>
    publish({ status: 'OFFLINE', latencyMs: null, checkedAt: Date.now() }),
  );
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void probe();
  });
}

/** The connection state, shared by every subscriber in the tab. */
export function useConnection(): ConnectionState {
  const [state, setState] = useState(current);

  useEffect(() => {
    ensureProbing();
    listeners.add(setState);
    setState(current);

    return () => {
      listeners.delete(setState);
    };
  }, []);

  return state;
}

export function ConnectionStatus(): React.JSX.Element {
  const connection = useConnection();
  const label =
    connection.status === 'OFFLINE'
      ? t('connection.offline')
      : connection.status === 'SLOW'
        ? t('connection.slow').replace('{ms}', String(connection.latencyMs ?? 0))
        : t('connection.online').replace('{ms}', String(connection.latencyMs ?? 0));

  return (
    <span
      className={`connection connection--${connection.status.toLowerCase()}`}
      title={label}
      aria-live="polite"
    >
      <span className="connection__dot" aria-hidden="true" />
      {connection.status !== 'ONLINE' && <span className="connection__label">{label}</span>}
    </span>
  );
}
